import { useState } from "react";
import { type Locale } from "@/shared/utils";
import { CALENDAR_LOCALE } from "./locale";
import type { DatePickerMode } from ".";

export interface HighlightedRange {
  start: Date;
  end: Date;
  label: string;
  sublabel?: string;
  color?: string;
}

interface RangeValue {
  start: Date | null;
  end: Date | null;
}

interface Props {
  month: Date;
  onSelect: (d: Date) => void;
  range: RangeValue;
  hoverDate?: Date | null;
  onHover?: (d: Date | null) => void;
  locale: Locale;
  mode?: DatePickerMode;
  minDate?: Date;
  maxDate?: Date;
  highlightedRanges?: HighlightedRange[];
}

export default function CalendarDays({
  month,
  onSelect,
  range,
  hoverDate,
  onHover,
  locale,
  mode = "range",
  minDate,
  maxDate,
  highlightedRanges,
}: Props) {
  const t = CALENDAR_LOCALE[locale];
  const isRange = mode === "range";
  const [hoveredHighlightIdx, setHoveredHighlightIdx] = useState<number | null>(null);

  const year = month.getFullYear();
  const m = month.getMonth();

  const firstDay = new Date(year, m, 1);
  const lastDay = new Date(year, m + 1, 0);

  const startOffset = (firstDay.getDay() || 7) - 1;
  const days: (Date | null)[] = [];

  for (let i = 0; i < startOffset; i++) days.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, m, d, 12, 0, 0, 0));

  const { start, end } = range;

  function isSameDay(a: Date | null, b: Date | null) {
    return a && b && a.toDateString() === b.toDateString();
  }

  function isInRange(d: Date) {
    if (!isRange) return false;

    if (start && end) return d > start && d < end;
    if (start && hoverDate) return d > start && d < hoverDate;
    return false;
  }

  function isDateDisabled(d: Date) {
    const dateToCheck = new Date(d);
    dateToCheck.setHours(0, 0, 0, 0);

    if (minDate) {
      const min = new Date(minDate);
      min.setHours(0, 0, 0, 0);
      if (dateToCheck < min) return true;
    }

    if (maxDate) {
      const max = new Date(maxDate);
      max.setHours(0, 0, 0, 0);
      if (dateToCheck > max) return true;
    }

    return false;
  }

  interface HighlightHit {
    label: string;
    sublabel?: string;
    color?: string;
  }

  function isDateHighlighted(d: Date): boolean {
    if (!highlightedRanges || highlightedRanges.length === 0) return false;
    const day = new Date(d);
    day.setHours(0, 0, 0, 0);
    for (const range of highlightedRanges) {
      const rStart = new Date(range.start);
      rStart.setHours(0, 0, 0, 0);
      const rEnd = new Date(range.end);
      rEnd.setHours(0, 0, 0, 0);
      if (day >= rStart && day <= rEnd) return true;
    }
    return false;
  }

  function getHighlightInfo(d: Date): {
    isHighlighted: boolean;
    hits: HighlightHit[];
    color?: string;
    isFirst: boolean;
    isLast: boolean;
  } {
    if (!highlightedRanges || highlightedRanges.length === 0)
      return { isHighlighted: false, hits: [], isFirst: false, isLast: false };
    const hits: HighlightHit[] = [];
    let firstColor: string | undefined;
    const dayStart = new Date(d);
    dayStart.setHours(0, 0, 0, 0);
    for (const range of highlightedRanges) {
      const rStart = new Date(range.start);
      rStart.setHours(0, 0, 0, 0);
      const rEnd = new Date(range.end);
      rEnd.setHours(0, 0, 0, 0);
      if (dayStart >= rStart && dayStart <= rEnd) {
        hits.push({ label: range.label, sublabel: range.sublabel, color: range.color });
        if (!firstColor && range.color) firstColor = range.color;
      }
    }
    const isHighlighted = hits.length > 0;
    const prevDay = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1, 12, 0, 0);
    const nextDay = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 12, 0, 0);
    return {
      isHighlighted,
      hits,
      color: firstColor,
      isFirst: isHighlighted && !isDateHighlighted(prevDay),
      isLast: isHighlighted && !isDateHighlighted(nextDay),
    };
  }

  const gridCell = "relative w-[2.125rem] h-8 flex items-center justify-center cursor-pointer";

  return (
    <div className="grid grid-cols-7 gap-y-1">
      {t.weekdays.map((w) => (
        <div key={w} className="w-[2.125rem] h-8 flex items-center justify-center text-body-regular-sm text-grey-400">
          {w}
        </div>
      ))}

      {days.map((d, i) => {
        if (!d) return <div key={i} className="w-[2.125rem] h-8" />;

        const isStart = isRange && isSameDay(d, start);
        const isEnd = isRange && isSameDay(d, end);

        const inRange = isRange ? isInRange(d) : false;

        const isHoverEnd = isRange && !end && hoverDate && isSameDay(d, hoverDate);
        const isSingleSelected = !isRange && isSameDay(d, start);
        const highlight = getHighlightInfo(d);
        const isDisabled = isDateDisabled(d);

        const showBg = isRange && (inRange || isStart || isEnd || isHoverEnd);

        const bgClasses = [
          "absolute inset-0",
          inRange ? "surface-brand-subtle" : "",
          isStart ? "rounded-l-lg surface-brand-subtle" : "",
          isEnd || isHoverEnd ? "rounded-r-lg surface-brand-subtle" : "",
        ].join(" ");

        const highlightRadius = highlight.isHighlighted
          ? highlight.isFirst && highlight.isLast
            ? "rounded-lg"
            : highlight.isFirst
              ? "rounded-l-lg"
              : highlight.isLast
                ? "rounded-r-lg"
                : ""
          : "";

        const isSelected = isRange ? isStart || isEnd || isHoverEnd : isSingleSelected;

        const fgClasses = [
          "relative z-10 w-[2.75rem] h-8 flex items-center justify-center",
          isSelected
            ? "radius-xs text-white primary-normal"
            : highlight.isHighlighted
              ? `${highlight.color ?? "background-notice-subtle"} content-base-secondary ${highlightRadius}`
              : `radius-xs ${isDisabled ? "text-grey-400 cursor-not-allowed opacity-50" : "content-base-primary"}`,
        ].join(" ");

        const showTooltip = highlight.isHighlighted && hoveredHighlightIdx === i;

        return (
          <div
            key={i}
            className="relative"
            onMouseEnter={() => {
              if (highlight.isHighlighted) setHoveredHighlightIdx(i);
              if (isRange && !isDisabled) onHover?.(d);
            }}
            onMouseLeave={() => {
              if (highlight.isHighlighted) setHoveredHighlightIdx(null);
              if (isRange) onHover?.(null);
            }}>
            <button
              type="button"
              disabled={isDisabled}
              onClick={(e) => {
                e.stopPropagation();
                if (!isDisabled) {
                  onSelect(d);
                }
              }}
              className={gridCell}>
              {showBg && <div className={bgClasses}></div>}

              <div className={fgClasses}>{d.getDate()}</div>
            </button>

            {showTooltip && (
              <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-1 background-static-white elevation-level-2 radius-xs p-2 w-max animate-fadeIn">
                <div className="flex flex-col gap-2">
                  {highlight.hits.map(function renderHit(hit, idx) {
                    return (
                      <div key={idx} className="flex flex-col gap-1">
                        <p className="text-label-xs content-base-primary whitespace-nowrap">{hit.label}</p>
                        {hit.sublabel && (
                          <div className="flex items-center gap-1.5">
                            <div
                              className={`w-2 h-2 radius-full shrink-0 ${hit.color ?? "background-notice-subtle"}`}
                            />
                            <span className="text-label-xs content-base-secondary whitespace-nowrap">
                              {hit.sublabel}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

